
<script setup>
import { useHead } from '@unhead/vue';
import OgImage from '../../images/blog/ripl/cover.png';

useHead({
	"title": "Brands We Love: RIPL Goods",
	"meta": [
		{
			"name": "description",
			"content": "Discover why Swagger partners with RIPL Goods. Sustainable merch that funds ocean cleanup, rainforest protection, and education. It is not just a product, it is a story."
		},
		{
			"rel": "canonical",
			"href": "https://giveswagger.com/swagger-brands-we-love-ripl"
		},
		{
			"property": "og:title",
			"content": "Brands We Love: RIPL Goods"
		},
		{
			"property": "og:description",
			"content": "Discover why Swagger partners with RIPL Goods. Sustainable merch that funds ocean cleanup, rainforest protection, and education. It is not just a product, it is a story."
		},
		{
			"property": "og:url",
			"content": "https://giveswagger.com/swagger-brands-we-love-ripl"
		},
		{
			"property": "og:image",
			"content": `https://giveswagger.com${OgImage}`
		}
	]
});
</script>
<template lang="pug">

article.page.page--simple
	header.page__header
		h1 Brands We Love: RIPL
	.page__content
		section.section
			.section__content
				.featured
					img(src='../../images/blog/ripl/ripl-1.png' alt='RIPL Goods logo overlaid on an ocean background, symbolizing their commitment to ocean conservation and global impact.')
				
				p Let’s be honest. Branded swag is everywhere, but not all of it leaves a lasting impression. But RIPL? They’re a different story. This certified B Corp is redefining what corporate gifting can look like with premium, design-forward products that are as purposeful as they are beautiful.
				p With every tote, bottle, and notebook, something real happens. Plastic is removed from coastlines. Rainforests are protected. Kids get to go to school.
				p It’s not just sustainable, it’s <i>impactful</i>. It’s swag that tells a story, makes a difference, and actually gets used. And that’s exactly why we’re obsessed.
				p At Swagger, we partner with brands that push swag forward. RIPL delivers substance, style, and storytelling with every single order. Here’s why we love them and why you will too.

				h2 The Visionary Behind the Brand
				img(src='../../images/blog/ripl/ripl-2.png')
				p RIPL was founded by Leo Bellis-Jones, a former ad guy from London who swapped deadlines for diving. After moving to Australia, he fell in love with the ocean. Surfing and freediving became his everyday rhythm, and that’s when he started noticing the plastic. The plastic crisis wasn’t abstract anymore. It was everywhere.
				p Leo launched RIPL with a simple goal: create products people actually use while driving measurable change for the planet. He didn’t want to just raise awareness. He wanted to build solutions you could hold in your hand. And that’s exactly what he did.

				h2 Swag That Shows Up and Gives Back
				img(src='../../images/blog/ripl/ripl-3.png')
				p Here’s where RIPL gets seriously cool. Their products aren’t just designed to look good, they’re mapped directly to impact. When someone receives a RIPL item, they’re not just getting a useful piece of swag. They’re funding real change.
				p Each core product supports one of three flagship programs:
				p <b>Rainforest:</b> Each RIPL notebook helps protect 1,000 square feet of tropical rainforest, one of the most biodiverse and endangered ecosystems on the planet.
				p <b>Education:</b> Every RIPL tote or tech bag funds a full week of school for a child in need. Real students. Real classrooms. Real opportunity.
				p <b>Ocean:</b> Every RIPL bottle funds one full day of employment for someone in a coastal community to collect plastic. That’s plastic pulled from shorelines and rivers before it reaches the ocean.
				p With every product, the impact is spelled out. Story tags and optional QR codes help brands show recipients exactly what their gift helped make happen. It’s swag with receipts.

				h2 The Product Lineup
				img(src='../../images/blog/ripl/ripl-4.png')
				p RIPL keeps things focused, which we love. No filler. No fluff. Just beautiful, functional pieces with story-first design and co-branding ready to go. Here are a few standouts:
				p <b>The RIPL Bottle:</b> Modern. Durable. Designed to make a dent in the plastic crisis. It’s an everyday essential with a side of environmental impact.
				p <b>The RIPL Tote and Tech Bag:</b> Made from sustainable fabrics and ready for the road, the office, or a weekend away. The fact that they fund education? Just the cherry on top.
				p <b>The RIPL Notebook:</b> Sleek and satisfying to write in. Made from recycled materials. Protects rainforest with every purchase. Not your average notebook.
				p More products are always in the pipeline, and each one is carefully crafted to deliver equal parts purpose and polish.
				
				h2 Why Swagger Is All In
				img(src='../../images/blog/ripl/ripl-5.png')
				p We’re not into throwaway swag. You know that. RIPL isn’t either. They make it easy to give something that’s not just cool or useful, but meaningful. Something your clients and employees will actually talk about. Something that says your brand stands for more.
				p When you gift RIPL, you’re not just checking a box. You’re joining a movement. And we think that’s pretty powerful.

				h2 Let’s Make Something That Matters
				p If you’re looking for swag that hits different, RIPL delivers. Whether you’re planning new hire kits, client thank-yous, or a brand campaign that needs a little extra heart, their products bring impact and intention to every moment.

				p Ready to create swag that looks good, does good, and tells a story? Reach out to us at <a href="mailto:hello@giveswagger.com">hello@giveswagger.com</a> or just fill out this short form:<router-link to="/get-started">Let’s Do This!</router-link>
				
</template>
  